/**
 * Audit Logger Service
 * FREE - Logs security events to Firestore
 */

import { collection, addDoc, serverTimestamp, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

export interface AuditLog {
  id?: string;
  userId: string;
  userEmail: string;
  action: string;
  resource: string;
  details?: any;
  ipAddress?: string;
  userAgent?: string;
  timestamp: any;
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  success: boolean;
  errorMessage?: string;
}

export class AuditLogger {
  /**
   * Log a security event
   */
  static async logEvent(
    userId: string,
    userEmail: string,
    action: string,
    resource: string,
    details?: any,
    severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL' = 'MEDIUM',
    success: boolean = true,
    errorMessage?: string
  ): Promise<void> {
    try {
      const auditLog: Omit<AuditLog, 'id'> = {
        userId,
        userEmail,
        action,
        resource,
        details: details ? JSON.stringify(details) : undefined,
        ipAddress: await this.getClientIP(),
        userAgent: navigator.userAgent,
        timestamp: serverTimestamp(),
        severity,
        success,
        errorMessage: errorMessage || undefined
      };

      await addDoc(collection(db, 'auditLogs'), auditLog);
      
      if (severity === 'HIGH' || severity === 'CRITICAL') {
        console.warn(`🚨 SECURITY EVENT: ${action} by ${userEmail}`, details);
      }
    } catch (error) {
      console.error('Failed to log audit event:', error);
    }
  }

  /**
   * Log login attempt
   */
  static async logLoginAttempt(
    userEmail: string,
    success: boolean,
    errorMessage?: string
  ): Promise<void> {
    await this.logEvent(
      'anonymous',
      userEmail,
      'LOGIN_ATTEMPT',
      'authentication',
      { email: userEmail },
      success ? 'LOW' : 'HIGH',
      success,
      errorMessage
    );
  }

  /**
   * Log user registration
   */
  static async logUserRegistration(
    userId: string,
    userEmail: string,
    invitationCode: string
  ): Promise<void> {
    await this.logEvent(
      userId,
      userEmail,
      'USER_REGISTRATION',
      'user_management',
      { invitationCode },
      'MEDIUM',
      true
    );
  }

  /**
   * Log invitation code usage
   */
  static async logInvitationCodeUsage(
    userId: string,
    userEmail: string,
    invitationCode: string,
    success: boolean
  ): Promise<void> {
    await this.logEvent(
      userId,
      userEmail,
      'INVITATION_CODE_USED',
      'invitation_system',
      { invitationCode },
      'MEDIUM',
      success
    );
  }

  /**
   * Log company creation
   */
  static async logCompanyCreation(
    userId: string,
    userEmail: string,
    companyId: string,
    companyName: string
  ): Promise<void> {
    await this.logEvent(
      userId,
      userEmail,
      'COMPANY_CREATED',
      'company_management',
      { companyId, companyName },
      'MEDIUM',
      true
    );
  }

  /**
   * Log contract creation
   */
  static async logContractCreation(
    userId: string,
    userEmail: string,
    contractId: string,
    contractNumber: string
  ): Promise<void> {
    await this.logEvent(
      userId,
      userEmail,
      'CONTRACT_CREATED',
      'contract_management',
      { contractId, contractNumber },
      'LOW',
      true
    );
  }

  /**
   * Log contract deletion
   */
  static async logContractDeletion(
    userId: string,
    userEmail: string,
    contractId: string,
    contractNumber: string
  ): Promise<void> {
    await this.logEvent(
      userId,
      userEmail,
      'CONTRACT_DELETED',
      'contract_management',
      { contractId, contractNumber },
      'MEDIUM',
      true
    );
  }

  /**
   * Log role change
   */
  static async logRoleChange(
    userId: string,
    userEmail: string,
    targetUserId: string,
    targetUserEmail: string,
    oldRole: string,
    newRole: string
  ): Promise<void> {
    await this.logEvent(
      userId,
      userEmail,
      'ROLE_CHANGED',
      'user_management',
      { targetUserId, targetUserEmail, oldRole, newRole },
      'HIGH',
      true
    );
  }

  /**
   * Log security violation
   */
  static async logSecurityViolation(
    userId: string,
    userEmail: string,
    violation: string,
    details?: any
  ): Promise<void> {
    await this.logEvent(
      userId,
      userEmail,
      'SECURITY_VIOLATION',
      'security',
      { violation, ...details },
      'CRITICAL',
      false,
      violation
    );
  }

  /**
   * Log session timeout
   */
  static async logSessionTimeout(
    userId: string,
    userEmail: string
  ): Promise<void> {
    await this.logEvent(
      userId,
      userEmail,
      'SESSION_TIMEOUT',
      'session_management',
      {},
      'MEDIUM',
      true
    );
  }

  /**
   * Get recent audit logs (for MASTER users)
   */
  static async getRecentLogs(limitCount: number = 100): Promise<AuditLog[]> {
    try {
      const q = query(
        collection(db, 'auditLogs'),
        orderBy('timestamp', 'desc'),
        limit(limitCount)
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as AuditLog));
    } catch (error) {
      console.error('Failed to get audit logs:', error);
      return [];
    }
  }

  /**
   * Get logs by user
   */
  static async getLogsByUser(userId: string, limitCount: number = 50): Promise<AuditLog[]> {
    try {
      const q = query(
        collection(db, 'auditLogs'),
        where('userId', '==', userId),
        orderBy('timestamp', 'desc'),
        limit(limitCount)
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as AuditLog));
    } catch (error) {
      console.error('Failed to get user audit logs:', error);
      return [];
    }
  }

  /**
   * Get client IP address (simplified)
   */
  private static async getClientIP(): Promise<string> {
    try {
      // This is a simplified approach - in production, you'd use a service
      return 'client-ip-not-available';
    } catch (error) {
      return 'unknown';
    }
  }
}
